import React from 'react';
import { Button, Input, Table, TagPicker, Tooltip, Whisper } from 'rsuite';
import * as groupsApi from '../../api/groups';
import * as coursesApi from '../../api/courses';
import ExportIcon from '@rsuite/icons/Export';
import DeleteIcon from '@rsuite/icons/Trash';
import CreateGroupModal from './CreateGroupModal';
import GroupStudentsListModal from './GroupStudentsListModal';
import { API_DOMAIN } from '../../config';
import PageLayout from '../../common/PageLayout';
import ColumnsButton from '../../common/ColumnsButton';
import FilterPanel, { FilterField } from '../../common/FilterPanel';
import { useTableColumns } from '../../common/useTableColumns';
import { useResourceList } from '../../common/useResourceList';
import { formatDate, getRowClassName } from '../../common/formatters';
import { renderRowNumberColumn } from '../../common/RowNumberColumn';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import TablePagination from '../../common/TablePagination';
import { useConfirm } from '../../common/useConfirm';

const { Column, HeaderCell, Cell } = Table;

const TABLE_COLUMNS = [
  { value: 'name', label: 'Назва', display: true, flexGrow: 1, minWidth: 120 },
  { value: 'typeName', label: 'Тип', display: true, flexGrow: 1, minWidth: 80 },
  {
    value: 'courseName',
    label: 'Курс',
    display: true,
    flexGrow: 2,
    minWidth: 140,
  },
  {
    value: 'createdAt',
    label: 'Створена',
    display: false,
    flexGrow: 1,
    minWidth: 100,
  },
  {
    value: 'finishedAt',
    label: 'Завершена',
    display: true,
    flexGrow: 1,
    minWidth: 110,
  },
];

type Group = {
  id: string;
  name: string;
  typeName: string;
  courseName: string;
  createdAt: number | null;
  finishedAt: number | null;
};

export default function Groups() {
  const [confirm, ConfirmDialog] = useConfirm();
  const clickedCellRef = React.useRef(false);
  const [name, setName] = React.useState('');
  const [coursesData, setCoursesData] = React.useState<
    { value: string; label: string }[]
  >([]);
  const [typesData, setTypesData] = React.useState<
    { value: number; label: string }[]
  >([]);
  const [courseIds, setCourseIds] = React.useState<string[]>([]);
  const [studentsListGroupId, setStudentsListGroupId] =
    React.useState<string>('');

  const [tableColumns, , toggleColumn] = useTableColumns(
    TABLE_COLUMNS,
    'groups:tableColumns'
  );

  const [updateModalObject, setUpdateModalObject] = React.useState<any | null>(
    null
  );
  const [createModalOpen, setCreateModalOpen] = React.useState<boolean>(false);

  const list = useResourceList<Group>({
    fetch: ({ page, perPage, sortColumn, sortOrder }) =>
      groupsApi.list(name, courseIds, page, perPage, sortColumn, sortOrder),
    filters: [name, courseIds],
    initialSortColumn: 'createdAt',
  });

  const getTagsPickersData = async () => {
    const { courses: coursesResponse } = await coursesApi.list(
      'updatedAt',
      'DESC'
    );
    const { types: coursesTypes } = await groupsApi.getTypes();
    setCoursesData(
      coursesResponse.map((c: any) => ({ value: c.id, label: c.name }))
    );
    setTypesData(
      coursesTypes.map((t: any) => ({ value: t.value, label: t.label }))
    );
  };

  React.useEffect(() => {
    getTagsPickersData().catch(console.error);
  }, []);

  const firstVisibleColumnValue = React.useMemo(
    () => tableColumns.find(column => column.display !== false)?.value,
    [tableColumns]
  );

  return (
    <>
      {ConfirmDialog}
      <PageLayout
        title="Групи"
        actions={
          <>
            <Input
              className="toolbar-search"
              placeholder="Пошук за назвою"
              value={name || ''}
              onChange={setName}
            />
            <FilterPanel
              activeCount={courseIds.length ? 1 : 0}
              onReset={() => setCourseIds([])}
            >
              {filterContainer => (
                <FilterField label="Курси">
                  <TagPicker
                    block
                    placeholder="Курси"
                    value={courseIds}
                    onChange={setCourseIds}
                    data={coursesData}
                    container={filterContainer}
                    preventOverflow
                  />
                </FilterField>
              )}
            </FilterPanel>
            <ColumnsButton columns={tableColumns} onToggle={toggleColumn} />
            <Button
              appearance="primary"
              onClick={() => setCreateModalOpen(true)}
            >
              Створити
            </Button>
          </>
        }
      >
        <div className="section-card">
          <Table
            style={{ width: '100%' }}
            wordWrap="break-word"
            loading={list.loading}
            autoHeight={true}
            affixHeader={true}
            affixHorizontalScrollbar={true}
            bordered={true}
            data={list.data}
            renderLoading={renderTableLoading}
            renderEmpty={renderTableEmpty}
            rowClassName={getRowClassName}
            onRowClick={rowData => {
              if (clickedCellRef.current) {
                clickedCellRef.current = false;
                return;
              }
              setStudentsListGroupId((rowData as Group).id);
            }}
            sortColumn={list.sortColumn}
            sortType={list.sortType}
            onSortColumn={(sortColumn, sortType) =>
              list.setSort(sortColumn, sortType || 'desc')
            }
          >
            {renderRowNumberColumn({ page: list.page, perPage: list.perPage })}

            {tableColumns.map(column => {
              if (column.display === false) {
                return null;
              }
              return (
                <Column
                  key={column.value}
                  flexGrow={column.flexGrow || 1}
                  minWidth={column.minWidth || 100}
                  fixed={
                    column.value === firstVisibleColumnValue
                      ? ('left' as const)
                      : undefined
                  }
                  fullText={true}
                  sortable={['finishedAt', 'createdAt'].includes(column.value)}
                >
                  <HeaderCell>{column.label}</HeaderCell>
                  {column.value === 'createdAt' ? (
                    <Cell dataKey={column.value}>
                      {rowData => formatDate(rowData[column.value])}
                    </Cell>
                  ) : column.value === 'finishedAt' ? (
                    <Cell dataKey={column.value}>
                      {rowData =>
                        rowData[column.value] ? (
                          <span className="status-tag status-finished">
                            {formatDate(rowData[column.value])}
                          </span>
                        ) : (
                          <span className="status-tag status-active">
                            Активна
                          </span>
                        )
                      }
                    </Cell>
                  ) : column.value === 'name' ? (
                    <Cell dataKey={column.value} style={{ fontWeight: 600 }} />
                  ) : (
                    <Cell dataKey={column.value} />
                  )}
                </Column>
              );
            })}

            <Column fixed="right" width={130}>
              <HeaderCell>Дії</HeaderCell>
              <Cell
                onClick={() => {
                  clickedCellRef.current = true;
                }}
                style={{ padding: '4px 6px' }}
              >
                {rowData => (
                  <div className="action-icons">
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Завершити групу</Tooltip>}
                    >
                      <Button
                        appearance="link"
                        onClick={async () => {
                          const result = await confirm({
                            message: `Ви впевнені, що хочете вдмітити групу ${rowData.name} (${rowData.courseName}) завершеною?`,
                            confirmText: 'Завершити',
                          });
                          if (!result) {
                            return;
                          }
                          groupsApi
                            .finished(rowData.id)
                            .catch(console.error)
                            .then(() => list.reload());
                        }}
                      >
                        <img width={18} src="/finish.svg" alt="Завершити" />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Експортувати список студентів</Tooltip>}
                    >
                      <a
                        href={`${API_DOMAIN}/api/groups/${rowData.id}/export`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExportIcon />
                      </a>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Управління студентами</Tooltip>}
                    >
                      <Button
                        appearance="link"
                        onClick={() => setStudentsListGroupId(rowData.id)}
                      >
                        <img width={18} src="/add_to_list.svg" alt="Студенти" />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Видалити групу</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        className="delete-btn"
                        onClick={async () => {
                          const result = await confirm({
                            message: `Ви впевнені, що хочете видалити групу ${rowData.name} (${rowData.courseName})?`,
                            destructive: true,
                            confirmText: 'Видалити',
                          });
                          if (!result) {
                            return;
                          }
                          groupsApi
                            .remove(rowData.id)
                            .catch(console.error)
                            .then(() => list.reload());
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </Whisper>
                  </div>
                )}
              </Cell>
            </Column>
          </Table>
          <TablePagination
            total={list.totalAmount}
            activePage={list.page}
            pages={list.totalPages}
            limit={list.perPage}
            limitOptions={[15, 30, 50, 100]}
            onChangeLimit={list.setPerPage}
            onChangePage={list.goToPage}
          />
        </div>
      </PageLayout>
      <CreateGroupModal
        open={createModalOpen}
        setOpen={setCreateModalOpen}
        coursesData={coursesData}
        typesData={typesData}
        callback={() => {
          list.reload();
        }}
      />
      <GroupStudentsListModal
        groupId={studentsListGroupId}
        setGroupId={setStudentsListGroupId}
      />
      {/*<CreateStudentModal open={createModalOpen} setOpen={setCreateModalOpen} coursesData={coursesData}*/}
      {/*                    callback={() => {*/}
      {/*                      getData(state.page).catch(console.error).then(() => setLoading(false));*/}
      {/*                    }}/>*/}
      {/*<StudentQueuesModal studentId={studentsListGroupId} setStudentId={setAddToQueueGroupId}*/}
      {/*                    coursesData={coursesData}/>*/}
      {/*<EditStudentModal obj={updateModalObject}*/}
      {/*                  setObj={setUpdateModalObject} callback={() => {*/}
      {/*  getData(state.page).catch(console.error).then(() => setLoading(false));*/}
      {/*}}/>*/}
    </>
  );
}
